import { config } from '@/config/runtime-config'

/**
 * Lazy Mapbox loader.
 *
 * A token is a per-deployment value and `config.mapboxToken` is empty by default,
 * so **"no map" is a supported state, not a failure**: every caller falls back to
 * `VectorPreview`, which needs no account, no network and no third party. Check
 * `hasMapbox()` before rendering a map, and keep the fallback path working — it
 * is what runs in CI, in an offline field deployment, and on the day a token is
 * rotated badly.
 */

export const hasMapbox = (): boolean => config.mapboxToken.trim().length > 0

type MapboxModule = typeof import('./mapbox-impl')['mapboxgl']

let pending: Promise<MapboxModule> | null = null

export function loadMapbox(): Promise<MapboxModule> {
  if (!hasMapbox()) {
    return Promise.reject(new Error('No Mapbox token is configured for this deployment.'))
  }
  // Memoised: several maps can mount at once (drawer + create dialog) and the
  // module must be evaluated exactly once.
  pending ??= import('./mapbox-impl')
    .then((mod) => {
      mod.mapboxgl.accessToken = config.mapboxToken
      return mod.mapboxgl
    })
    .catch((err) => {
      pending = null              // let a later mount retry after a network blip
      throw err
    })
  return pending
}

/** Kenya, as a Mapbox `LngLatBoundsLike`. The same box the importer validates against. */
export const KENYA_BOUNDS: [[number, number], [number, number]] = [
  [33.9, -4.7],
  [41.9, 5.5],
]

/**
 * Which basemap to draw.
 *
 * `theme` follows the app's `.dark` class so a map does not glow white inside a
 * dark shell. `satellite` is offered wherever someone is placing a polling
 * station: "the primary school with the blue roof" is findable on imagery and
 * invisible on a street map.
 */
export type MapStyleChoice = 'theme' | 'streets' | 'satellite'

/**
 * `streets` is the third option because the other two answer different questions.
 *
 * The themed basemap is deliberately quiet — it exists so a map does not shout inside the
 * app's chrome, and it drops most street names to get there. That is right for a
 * choropleth and wrong for "which road was the roadblock on". Satellite shows the ground
 * truth but no labels at all. A reporter placing an incident needs the named streets, so
 * the standard Mapbox street style is offered alongside, and it is the same style in both
 * themes: a street map that inverts to dark is harder to read against imagery, not easier.
 */
export function styleUrl(choice: MapStyleChoice, dark: boolean): string {
  if (choice === 'satellite') return config.mapboxStyleSatellite
  if (choice === 'streets') return config.mapboxStyleStreets
  return dark ? config.mapboxStyleDark : config.mapboxStyleLight
}
